import { Col, message, Table, Row, TableProps, Grid, Spin } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import config from 'config';
import {
  useGetInvitationsListQuery,
  useDeleteInvitationMutation,
  InvitationOutput,
  InvitationListSort,
  Order,
  InvitationsListFilterInput,
  PermissionsEnum,
} from 'gql/types';
import { TableSearchInput } from 'modules/common/components';
import { useSort, useDebounce, useFilter } from 'modules/common/hooks';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { useTranslation } from 'react-i18next';

const { AddUsers } = PermissionsEnum;

import { getColumns } from './columns';

const { useBreakpoint } = Grid;

const InvitationsTable = () => {
  const { t } = useTranslation('admin.InvitationsPage');
  const currentUser = useCurrentUser();
  const canDelete = currentUser.isCan(AddUsers);
  const pagination = usePagination();
  const sort = useSort<InvitationListSort>();
  const filter = useFilter<InvitationsListFilterInput>();
  const { md } = useBreakpoint();

  const getInvitationsListQuery = useGetInvitationsListQuery({
    variables: {
      input: {
        filter: filter.getFilterObject(),
        take: config.PAGINATION.DEFAULT_LIMIT,
        skip: pagination.skip,
        sort: sort.options,
      },
    },
    onCompleted: (data) => {
      pagination.setTotal(data.getInvitationsList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const invitations =
    getInvitationsListQuery.data?.getInvitationsList.data || [];
  const isLoading = getInvitationsListQuery.loading;

  const [deleteInvitation, deleteInvitationMutation] =
    useDeleteInvitationMutation({
      update(cache) {
        cache.evict({ fieldName: 'getInvitationsList' });
      },
      onCompleted: () => {
        message.success(t('successfullyDeleted'));
      },
      onError: (error) => message.error(error.message),
    });

  const deleteHandler = (id: string) => {
    deleteInvitation({ variables: { id } });
  };

  const columns = getColumns({
    deleteHandler,
    canDelete,
    canUpdateUserInfo: false,
    currentUserEmail: currentUser.data?.email,
    showSorterTooltip: md,
    sort: sort?.options[0],
  });

  const { debounce } = useDebounce();
  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchingQuery',
      value: value || undefined,
    });
  };

  const onTableChange: TableProps<InvitationOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<InvitationOutput>;
      const fieldName = tableSorter.columnKey as InvitationListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        sort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        sort.removeSortOption(fieldName);
      }
    }
  };

  return (
    <Spin spinning={isLoading || deleteInvitationMutation.loading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Row gutter={[16, 10]} justify="space-between" align="middle">
            <Col flex={1}>
              <TableSearchInput
                onSearchChange={debounce(handleSearch)}
                placeholder={t('searchInvitation')}
                maxLength={100}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Table
            rowKey={({ id }) => id}
            columns={columns}
            dataSource={invitations}
            onChange={onTableChange}
            scroll={{ x: 750 }}
            pagination={pagination.config}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default InvitationsTable;
